import { useApolloClient } from '@apollo/client';
import { Typography, useTheme } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import {
  CONVERSATION_SNOOZE_PRESET_LABELS,
  ConversationSnoozePreset,
  ConversationStatus,
} from '@provision/conversation-types';
import { nextMondayAtNine } from '@provision/sidekick-utils';
import dayjs from 'dayjs';
import { memo, useCallback, useMemo, useState } from 'react';
import { hiddenPickerSx } from 'src/components/DatePicker/hidden-picker.styles';
import { ModalContainer, ModalList, type ModalListItem } from 'src/components/ModalContainer';
import { TimeFormats, usePartners, useSnoozeConversation } from 'src/graphql';
import { getCachedConversation } from 'src/graphql/entities/conversation/conversation';
import { useRestoreConversationStatus } from 'src/graphql/entities/conversation/use-restore-conversation-status';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { getFormattedDate, getHumanReadableDate } from 'src/utils/date';
import { useDisplayTime, useNotifications } from 'src/utils/hooks';
import {
  ensureFutureDate,
  getDateFromNaturalLanguage,
  resolveNaturalLanguageDate,
} from 'src/utils/natural-language-date';
import { capitalizeString } from 'src/utils/strings';

type SnoozeDate = (timezone?: string) => string;

interface SnoozePreset {
  id: string;
  text: string;
  date: SnoozeDate;
}

interface SnoozeOption extends ModalListItem {
  date?: SnoozeDate;
}

const CUSTOM_OPTION_ID = 'custom';

const ONE_MONTH_FROM_NOW =
  CONVERSATION_SNOOZE_PRESET_LABELS[ConversationSnoozePreset.OneMonth];

const PRESETS: SnoozePreset[] = [
  {
    id: 'an-hour',
    text: 'An hour from now',
    date: () => getDateFromNaturalLanguage('An hour from now'),
  },
  {
    id: 'tomorrow',
    text: CONVERSATION_SNOOZE_PRESET_LABELS[ConversationSnoozePreset.TomorrowAtNine],
    date: (timezone) => getDateFromNaturalLanguage('Tomorrow at 9am', { timezone }),
  },
  {
    id: 'next-monday',
    text: CONVERSATION_SNOOZE_PRESET_LABELS[ConversationSnoozePreset.NextWeek],
    date: (timezone) => nextMondayAtNine(new Date(), timezone)?.toISOString() ?? '',
  },
  {
    id: 'a-month',
    text: ONE_MONTH_FROM_NOW,
    date: () =>
      getDateFromNaturalLanguage(ONE_MONTH_FROM_NOW, {
        dayStart: new Date().toLocaleTimeString(),
      }),
  },
];

function parseSnoozeDate(text: string): SnoozeDate | null {
  const withDayStart = getDateFromNaturalLanguage(text, { dayStart: '9:00am' });
  if (!withDayStart) {
    return null;
  }
  const withoutDayStart = getDateFromNaturalLanguage(text);
  return () =>
    ensureFutureDate(resolveNaturalLanguageDate(withDayStart, withoutDayStart), text);
}

export const SnoozeConversationModal = () => {
  const theme = useTheme();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [input, setInput] = useState('');
  const [customDate, setCustomDate] = useState<dayjs.Dayjs | null>(dayjs());

  const { timeFormat, timeFormatPref } = useDisplayTime();
  const { isOpened, modalProps } = useModalState(Modals.SnoozeConversationModal);
  const { closeModal } = useModalMethods(Modals.SnoozeConversationModal);
  const { snoozeConversation } = useSnoozeConversation();
  const restoreConversationStatus = useRestoreConversationStatus();
  const { cache } = useApolloClient();
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const { partners } = usePartners();

  const { conversationStatusId, snoozeUntil, partnerId, friendlyId, conversationId } =
    modalProps || {};
  const timezone = partners?.[partnerId ?? '']?.timeZone;

  const options = useMemo<SnoozeOption[]>(() => {
    const toOption = (id: string, title: string, date: SnoozeDate): SnoozeOption => ({
      id,
      title,
      date,
      endAdornment: (
        <Typography variant="caption" color="text.secondary" noWrap>
          {getFormattedDate(date(timezone), timeFormat)}
        </Typography>
      ),
    });

    if (input) {
      const parsedDate = parseSnoozeDate(input);
      return parsedDate
        ? [
            toOption(
              'parsed',
              capitalizeString(getHumanReadableDate(parsedDate(timezone))),
              parsedDate,
            ),
          ]
        : [];
    }

    const presets = PRESETS.map(({ id, text, date }) => toOption(id, text, date));
    if (
      conversationStatusId !== ConversationStatus.Snoozed &&
      snoozeUntil &&
      dayjs().isBefore(dayjs(snoozeUntil))
    ) {
      presets.push(
        toOption('last-snoozed', 'Last Snoozed Date', () => dayjs(snoozeUntil).toISOString()),
      );
    }
    return [...presets, { id: CUSTOM_OPTION_ID, title: 'Custom' }];
  }, [input, timezone, timeFormat, conversationStatusId, snoozeUntil]);

  const submitSnoozeConversation = useCallback(
    async (date?: string) => {
      if (!date || !conversationId) {
        return;
      }

      const diff = new Date(date).getTime() - new Date().getTime();
      if (diff <= 0) {
        showErrorAlert(
          `Invalid date ${dayjs.duration(diff).humanize()} ago. The snooze date cannot be earlier than or equal to current's date`,
        );

        return;
      }

      closeModal();

      // read before the snooze: its optimistic write replaces the status in the cache
      const cached = getCachedConversation(cache, conversationId);
      const previous = {
        conversationId,
        conversationStatusId: cached?.conversationStatusId ?? conversationStatusId,
        snoozeUntil: cached?.snoozeUntil ?? snoozeUntil,
      };

      const response = await snoozeConversation({
        snoozeUntil: date,
        conversationId,
      });

      if (!response?.success) {
        const genericMessage =
          'Something went wrong snoozing the conversation, please try again';
        const err = response?.error ?? '';
        const isValidationOrNotFound =
          err.includes('snoozeUntil') || err.includes('Could not find');
        showErrorAlert(isValidationOrNotFound ? err : genericMessage);
        return;
      }

      const { conversationStatusId: previousStatusId } = previous;
      showSuccessAlert(`Conversation snoozed for ${dayjs.duration(diff).humanize()}`, {
        onUndo: previousStatusId
          ? () =>
              void restoreConversationStatus({
                ...previous,
                conversationStatusId: previousStatusId,
              })
          : undefined,
      });
    },
    [
      cache,
      closeModal,
      conversationId,
      conversationStatusId,
      restoreConversationStatus,
      showErrorAlert,
      showSuccessAlert,
      snoozeConversation,
      snoozeUntil,
    ],
  );

  const handleSelect = (option: SnoozeOption) => {
    if (option.id === CUSTOM_OPTION_ID) {
      setPickerOpen(true);
      return;
    }
    void submitSnoozeConversation(option.date?.(timezone));
  };

  return (
    <ModalContainer
      open={Boolean(conversationId) && isOpened}
      size="medium"
      title={friendlyId ? `Snooze ${friendlyId}` : 'Snooze'}
      onClose={closeModal}
      data-testid="snooze-modal"
    >
      <ModalList
        items={options}
        shouldFilter={false}
        onSearchChange={setInput}
        onSelect={handleSelect}
        placeholder={`Try ${timeFormatPref === TimeFormats.TwelveHour ? '8am' : '20:00'}, in 3 days, Aug 16th`}
        emptyText="No date matches that text."
        testId="snooze-modal"
      />
      <DateTimePicker
        label="Custom time picker"
        disablePast
        open={pickerOpen}
        value={customDate}
        onChange={(newValue) => setCustomDate(newValue)}
        closeOnSelect={false}
        onOpen={() => setPickerOpen(true)}
        onClose={() => {
          setPickerOpen(false);
          setCustomDate(dayjs());
        }}
        slotProps={{
          actionBar: {
            onAccept: async () => {
              setPickerOpen(false);
              await submitSnoozeConversation(customDate?.toISOString());
              setCustomDate(dayjs());
            },
          },
          popper: {
            sx: {
              '& .MuiMultiSectionDigitalClockSection-root': {
                maxHeight: theme.utils.important('auto'),
              },

              '& .MuiMultiSectionDigitalClockSection-root::after': {
                height: theme.utils.important('auto'),
              },
            },
          },
        }}
        aria-hidden="true"
        sx={hiddenPickerSx}
      />
    </ModalContainer>
  );
};

export default memo(SnoozeConversationModal);
